// A wide table in its own box (Iris's finding F1, approved 2026-10-24): where the columns do not fit, the table scrolls
// sideways inside the box, never the page; the cut edge fades, "+ n columns" at the top right says how many are out of
// view, and the first column (the name) stays put while the rest scroll under it. At a width where everything fits,
// nothing shows. The Price grids, the Materials and Subs tables and the Scope dot grid use it.

import { useEffect, useRef, useState, type ReactNode } from 'react';

export function Wide({ children, className, sticky = true, label = 'columns' }: { children: ReactNode; className?: string; /** The first column stays put while the rest scroll (off for a table whose first column is not a name). */ sticky?: boolean; /** The word for what is cut off: "columns", or "builds". */ label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState({ right: 0, left: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const table = el.querySelector('table');
      const heads = table ? Array.from(table.querySelectorAll('thead tr:last-child > th, thead tr:last-child > td')) as HTMLElement[] : [];
      const edge = el.scrollLeft + el.clientWidth;
      // a column is out of view when more than a third of it sits past the box's right edge
      const right = heads.filter((h) => h.offsetLeft + h.offsetWidth / 3 > edge).length;
      setCut({ right: el.scrollWidth - edge > 2 ? right : 0, left: el.scrollLeft > 2 });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const table = el.querySelector('table');
    if (table) ro.observe(table);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [children]);
  return (
    <div className={`widew${sticky ? ' sticky' : ''}${cut.right > 0 ? ' cutr' : ''}${cut.left ? ' cutl' : ''}${className ? ` ${className}` : ''}`}>
      <div ref={ref} className="wide">
        {children}
      </div>
      {cut.right > 0 && (
        <span className="more" aria-live="polite">
          + {cut.right} {cut.right === 1 ? label.replace(/s$/, '') : label} →
        </span>
      )}
    </div>
  );
}
